import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * Valeur générique d'un groupe de référentiel.
 * metadataJson est un JSON libre sérialisé en chaîne, à parser côté appelant.
 */
export interface ReferentialItem {
  id: string;
  groupCode: string;
  code: string;
  label: string;
  description: string | null;
  sortOrder: number;
  metadataJson: string | null;
  active: boolean;
  systemDefined?: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface ReferentialGroup {
  id: string;
  code: string;
  name: string;
  description: string | null;
  version: number;
  active: boolean;
  systemDefined: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface CreateReferentialGroupPayload {
  code: string;
  name: string;
  description?: string | null;
  active: boolean;
}

export interface CreateReferentialItemPayload {
  code: string;
  label: string;
  description?: string | null;
  sortOrder?: number | null;
  metadataJson?: string | null;
  active: boolean;
}

export interface UpdateReferentialItemPayload {
  label: string;
  description?: string | null;
  sortOrder?: number | null;
  metadataJson?: string | null;
  active: boolean;
}

export interface ChangeReferentialStatusPayload {
  active: boolean;
  reason?: string | null;
}

export interface LocationRegion {
  id: string;
  code: string;
  name: string;
  countryCode: string;
  sortOrder: number;
  active: boolean;
}

export interface LocationCity {
  id: string;
  regionCode: string;
  code: string;
  name: string;
  sortOrder: number;
  active: boolean;
}

export interface CreateRegionPayload {
  code: string;
  name: string;
  countryCode: string;
  sortOrder?: number | null;
  active: boolean;
}

export interface UpdateRegionPayload {
  name: string;
  countryCode: string;
  sortOrder?: number | null;
  active: boolean;
}

export interface CreateCityPayload {
  code: string;
  name: string;
  sortOrder?: number | null;
  active: boolean;
}

export interface UpdateCityPayload {
  name: string;
  sortOrder?: number | null;
  active: boolean;
}

/** Référentiels partagés (referential-service), consultation publique et administration. */
@Injectable({ providedIn: 'root' })
export class ReferentialService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/referential-service/api/v1/referentials`;

  listGroups(): Observable<ReferentialGroup[]> {
    return this.http.get<ReferentialGroup[]>(`${this.baseUrl}/groups`);
  }

  createGroup(payload: CreateReferentialGroupPayload): Observable<ReferentialGroup> {
    return this.http.post<ReferentialGroup>(`${this.baseUrl}/groups`, payload);
  }

  /** Valeurs d'un groupe, triées par sortOrder puis label côté backend. */
  groupItems(groupCode: string, includeInactive = false): Observable<ReferentialItem[]> {
    const params = new HttpParams().set('includeInactive', includeInactive);
    return this.http.get<ReferentialItem[]>(`${this.baseUrl}/groups/${groupCode}/items`, { params });
  }

  createItem(groupCode: string, payload: CreateReferentialItemPayload): Observable<ReferentialItem> {
    return this.http.post<ReferentialItem>(`${this.baseUrl}/groups/${groupCode}/items`, payload);
  }

  updateItem(itemId: string, payload: UpdateReferentialItemPayload): Observable<ReferentialItem> {
    return this.http.put<ReferentialItem>(`${this.baseUrl}/items/${itemId}`, payload);
  }

  changeItemStatus(itemId: string, payload: ChangeReferentialStatusPayload): Observable<ReferentialItem> {
    return this.http.patch<ReferentialItem>(`${this.baseUrl}/items/${itemId}/status`, payload);
  }

  listRegions(countryCode = 'CM', includeInactive = true): Observable<LocationRegion[]> {
    const params = new HttpParams().set('countryCode', countryCode).set('includeInactive', includeInactive);
    return this.http.get<LocationRegion[]>(`${this.baseUrl}/locations/regions`, { params });
  }

  createRegion(payload: CreateRegionPayload): Observable<LocationRegion> {
    return this.http.post<LocationRegion>(`${this.baseUrl}/locations/regions`, payload);
  }

  updateRegion(regionId: string, payload: UpdateRegionPayload): Observable<LocationRegion> {
    return this.http.put<LocationRegion>(`${this.baseUrl}/locations/regions/${regionId}`, payload);
  }

  changeRegionStatus(regionId: string, payload: ChangeReferentialStatusPayload): Observable<LocationRegion> {
    return this.http.patch<LocationRegion>(`${this.baseUrl}/locations/regions/${regionId}/status`, payload);
  }

  listCities(regionCode: string, includeInactive = true): Observable<LocationCity[]> {
    const params = new HttpParams().set('includeInactive', includeInactive);
    return this.http.get<LocationCity[]>(`${this.baseUrl}/locations/regions/${regionCode}/cities`, { params });
  }

  createCity(regionCode: string, payload: CreateCityPayload): Observable<LocationCity> {
    return this.http.post<LocationCity>(`${this.baseUrl}/locations/regions/${regionCode}/cities`, payload);
  }

  updateCity(cityId: string, payload: UpdateCityPayload): Observable<LocationCity> {
    return this.http.put<LocationCity>(`${this.baseUrl}/locations/cities/${cityId}`, payload);
  }

  changeCityStatus(cityId: string, payload: ChangeReferentialStatusPayload): Observable<LocationCity> {
    return this.http.patch<LocationCity>(`${this.baseUrl}/locations/cities/${cityId}/status`, payload);
  }
}
